import { z } from 'zod';
import {
  ConsolidationPassNameSchema,
  LEGACY_REVIEW_PASS_NAME_ACCEPTED_FOR_PAYLOAD_SCHEMA,
  ReviewExecutionPhaseSchema,
  REVIEW_PASS_ORDER,
} from './review-payloads.js';
import { CaseWikiContextUserInputsSchema } from './context-user-inputs.js';
import { CaseWikiLlmRoutingSchema } from './wiki-dispatch.js';

export const CaseWikiPipelinePhaseSchema = z.enum([
  'ingest',
  'lint',
  'consolidation',
  'review',
]);
export type CaseWikiPipelinePhase = z.infer<typeof CaseWikiPipelinePhaseSchema>;

export const CaseWikiPipelineTriggerSourceSchema = z.enum([
  'manual',
  'rerun',
  'resume',
  'reset',
  'case_discovery',
  'user_ui',
  'edition',
  'nightly_catchup',
]);
export type CaseWikiPipelineTriggerSource = z.infer<typeof CaseWikiPipelineTriggerSourceSchema>;

/** Sources that always carry an attributable lawyer or legacy actor UUID. */
export const CaseWikiPipelineUserTriggerSourceSchema = z.enum([
  'manual',
  'rerun',
  'resume',
  'reset',
  'case_discovery',
  'user_ui',
  'edition',
]);
export type CaseWikiPipelineUserTriggerSource = z.infer<
  typeof CaseWikiPipelineUserTriggerSourceSchema
>;

/**
 * BullMQ payload for the `case-wiki-pipeline` parent queue job.
 *
 * The parent is the durable orchestration boundary. It accepts trigger sources
 * that are not yet valid child trigger sources (`reset`, `case_discovery`) so
 * later route/tool migrations do not need another shared contract change.
 */
const CaseWikiPipelineJobPayloadBaseSchema = z.object({
  caseId: z.string().uuid(),
  cabinetId: z.string().uuid(),
  requestId: z.string().min(1),
  pipelineRunId: z.string().uuid(),
  pipelineJobId: z.string().min(1),
  fromPhase: CaseWikiPipelinePhaseSchema,
  caseWikiContextUserInputs: CaseWikiContextUserInputsSchema.optional(),
  documentIds: z.array(z.string().uuid()).max(200).optional(),
  passes: z
    .array(z.union([
      ConsolidationPassNameSchema,
      LEGACY_REVIEW_PASS_NAME_ACCEPTED_FOR_PAYLOAD_SCHEMA,
    ]))
    .min(1)
    .max(6)
    .optional(),
  step: CaseWikiPipelinePhaseSchema.optional(),
  /**
   * Routage LLM décidé par le plan de nuit (cf. `CaseWikiLlmRoutingSchema`).
   * Optionnel : absent = `box`, pour que les parents déjà en file avant l'ajout
   * du champ continuent de parser et gardent le comportement d'avant.
   */
  routing: CaseWikiLlmRoutingSchema.optional(),
  /**
   * Parent lancé par le dispatcher de nuit (demande programmée, quelle que soit
   * sa source). Son ingestion passe derrière celle d'un lancement direct de
   * l'avocat dans la file `case-wiki`. Absent = lancement direct.
   */
  fromNightDispatch: z.literal(true).optional(),
});

/**
 * The nightly catch-up is the sole unattended parent pipeline. Its null actor
 * is explicit instead of a synthetic user ID, while every lawyer-initiated or
 * historical source retains a UUID for auditability and compatibility.
 */
const CaseWikiPipelineJobWithUserSchema = CaseWikiPipelineJobPayloadBaseSchema.extend({
  userId: z.string().uuid(),
  triggerSource: CaseWikiPipelineUserTriggerSourceSchema,
});

const CaseWikiNightlyCatchupPipelineJobSchema = CaseWikiPipelineJobPayloadBaseSchema.extend({
  userId: z.null(),
  triggerSource: z.literal('nightly_catchup'),
});

export const CaseWikiPipelineJobPayloadSchema = z.discriminatedUnion('triggerSource', [
  CaseWikiPipelineJobWithUserSchema,
  CaseWikiNightlyCatchupPipelineJobSchema,
]);
export type CaseWikiPipelineJobPayload = z.infer<typeof CaseWikiPipelineJobPayloadSchema>;

/**
 * BullMQ payload for the `case-wiki-ingest` queue.
 *
 * Enqueued by the `case-wiki-pipeline` parent job. Legacy direct jobs may
 * still exist in Redis during the 2026-06-14 cutover; processors reject those
 * explicitly when `pipelineRunId` is absent so they cannot resurrect a stopped
 * pre-orchestrator run.
 *
 * `triggerSource` is optional so legacy jobs enqueued before the field was
 * added keep parsing; the worker defaults to `'manual'` when absent.
 */
const CaseWikiIngestJobPayloadBaseSchema = z.object({
  caseId: z.string().uuid(),
  cabinetId: z.string().uuid(),
  requestId: z.string().min(1),
  pipelineRunId: z.string().uuid().optional(),
  caseWikiContextUserInputs: CaseWikiContextUserInputsSchema.optional(),
  /**
   * Optional allowlist of document ids to ingest. When omitted, every
   * not-yet-ingested piece of the case is processed. Capped at 200 to
   * avoid oversized BullMQ payloads; larger allowlists hit the same ceiling
   * as a full-case run and should just drop the filter.
   */
  documentIds: z.array(z.string().uuid()).max(200).optional(),
  /** Hérité du parent ; absent = `box`. Lu par le worker pour filtrer la chaîne. */
  routing: CaseWikiLlmRoutingSchema.optional(),
});

const CaseWikiIngestUserActorSchema = z.object({
  userId: z.string().uuid(),
  triggerSource: z.enum(['manual', 'auto', 'rerun', 'resume']).optional(),
});

const CaseWikiNightlyChildActorSchema = z.object({
  // WHY: unattended catch-up has no lawyer actor. Keeping the null coupled to
  // its server-owned source prevents any user-triggered job from losing audit
  // attribution merely because `userId` was made broadly nullable.
  userId: z.null(),
  triggerSource: z.literal('nightly_catchup'),
});

export const CaseWikiIngestJobPayloadSchema = z.union([
  CaseWikiIngestJobPayloadBaseSchema.and(CaseWikiIngestUserActorSchema),
  CaseWikiIngestJobPayloadBaseSchema.and(CaseWikiNightlyChildActorSchema),
]);
export type CaseWikiIngestJobPayload = z.infer<typeof CaseWikiIngestJobPayloadSchema>;

/**
 * BullMQ payload for the `case-wiki-review` queue (PR2.2 / TICKET-124).
 *
 * La queue reste physiquement nommée `case-wiki-review`, mais elle transporte
 * deux phases logiques : consolidation puis review. Les jobs legacy sans
 * `phase` restent parsables comme `review` pendant la vidange Redis.
 */
const CaseWikiReviewJobBasePayloadSchema = z.object({
  caseId: z.string().uuid(),
  cabinetId: z.string().uuid(),
  requestId: z.string().min(1),
  pipelineRunId: z.string().uuid().optional(),
  caseWikiContextUserInputs: CaseWikiContextUserInputsSchema.optional(),
  /** Hérité du parent ; absent = `box`. Lu par le worker pour filtrer la chaîne. */
  routing: CaseWikiLlmRoutingSchema.optional(),
});

const CaseWikiReviewUserActorSchema = z.object({
  userId: z.string().uuid(),
  triggerSource: z.enum(['manual', 'auto_post_ingest', 'rerun', 'resume']).optional(),
});

const CaseWikiReviewActorSchema = z.union([
  CaseWikiReviewUserActorSchema,
  CaseWikiNightlyChildActorSchema,
]);

const CaseWikiConsolidationChildPayloadSchema = CaseWikiReviewJobBasePayloadSchema.extend({
  phase: z.literal('consolidation'),
  // [] is a deliberate no-op after filtering a parent selection for this phase.
  passes: z.array(ConsolidationPassNameSchema).max(3).optional(),
});

const CaseWikiReviewChildPayloadSchema = CaseWikiReviewJobBasePayloadSchema.extend({
  phase: z.literal('review').optional().default('review'),
  // [] is a deliberate no-op; undefined means "run every active review pass".
  passes: z
    .array(LEGACY_REVIEW_PASS_NAME_ACCEPTED_FOR_PAYLOAD_SCHEMA)
    .max(3)
    .optional(),
});

export const CaseWikiReviewJobPayloadSchema = z
  .intersection(
    z.union([CaseWikiConsolidationChildPayloadSchema, CaseWikiReviewChildPayloadSchema]),
    CaseWikiReviewActorSchema,
  )
  .transform((payload) => {
    if (payload.phase === 'consolidation') return payload;
    const activePasses = payload.passes?.filter((pass) =>
      REVIEW_PASS_ORDER.includes(pass),
    );
    return {
      ...payload,
      phase: ReviewExecutionPhaseSchema.parse('review'),
      ...(activePasses !== undefined ? { passes: activePasses } : { passes: undefined }),
    };
  });
export type CaseWikiReviewJobPayload = z.infer<typeof CaseWikiReviewJobPayloadSchema>;

/**
 * BullMQ payload for the `case-wiki-lint` queue (PR3 / TICKET-124).
 *
 * Linter : SQL scan + LLM corrections. Pas de paramétrage spécifique pour l'instant
 * (le scope = tout le dossier).
 */
const CaseWikiLintJobPayloadBaseSchema = z.object({
  caseId: z.string().uuid(),
  cabinetId: z.string().uuid(),
  requestId: z.string().min(1),
  pipelineRunId: z.string().uuid().optional(),
  caseWikiContextUserInputs: CaseWikiContextUserInputsSchema.optional(),
  /** Hérité du parent ; absent = `box`. Lu par le worker pour filtrer la chaîne. */
  routing: CaseWikiLlmRoutingSchema.optional(),
});

export const CaseWikiLintJobPayloadSchema = z.union([
  CaseWikiLintJobPayloadBaseSchema.and(CaseWikiReviewUserActorSchema),
  CaseWikiLintJobPayloadBaseSchema.and(CaseWikiNightlyChildActorSchema),
]);
export type CaseWikiLintJobPayload = z.infer<typeof CaseWikiLintJobPayloadSchema>;
